/**
 * PasswordCreator — shared, interactive password + confirm UX.
 *
 * Mirrors the NIST 800-63B rules enforced server-side in
 * `backend/password_policy.py`. Used on both `/auth/recovery`
 * (reset) and `/signup` (new account).
 *
 * Contract
 * ────────
 * Props:
 *   value            (string, required)     — controlled password
 *   onValueChange    (fn, required)         — (newPassword) => void
 *   confirmValue     (string, required)     — controlled confirm password
 *   onConfirmChange  (fn, required)         — (newConfirm)  => void
 *   onValidChange    (fn, optional)         — (bool)        => void  fires whenever
 *                                              the "everything is green" flag flips
 *   email            (string, optional)     — enables the "no email in password" rule
 *   name             (string, optional)     — enables the "no name in password" rule
 *   variant          ('dark' | 'light')     — visual theme, defaults to 'dark'
 *   idPrefix         (string, optional)     — prefix for data-testids so multiple
 *                                              instances on the same page don't clash
 *
 * The parent stays in charge of the submit button — this component
 * ONLY manages the fields, rules, meter, and validity signal.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, Check, X } from 'lucide-react';

/* Mirror `backend/password_policy.py::_BLOCKLIST` (trimmed but keeps
   the top-flagged entries — any near-miss is caught server-side too). */
const BLOCKLIST = new Set([
  'password', '123456', '12345678', '1234567890', 'qwerty', 'abc123',
  '111111', 'letmein', 'welcome', 'admin', 'admin123', 'passw0rd',
  'password1', 'password123', 'iloveyou', 'monkey', 'dragon', 'master',
  '12345', 'qwertyuiop', '1q2w3e4r', 'qazwsx', 'trustno1', 'sunshine',
  'princess', 'starwars', 'freedom', 'vigilant', 'vigilant2026',
  'vigilant@2026', 'amer', 'test', 'test123', 'demo', 'demo123',
  'changeme', 'default', 'secret', 'root', 'toor', 'superuser',
  '111111111', '000000', '987654321', '1qaz2wsx', 'qweasd', 'pass',
  'passw', 'passwd', 'p@ssword', 'p@ssw0rd',
]);

/* Build the rule set. The email/name-based rules are only registered
   when we have that context — RecoveryLanding doesn't know the user's
   email at the point of typing, so those rules are hidden there.  */
function buildRules({ email, name }) {
  const rules = [
    { id: 'len',       label: 'At least 12 characters',   test: (p) => p.length >= 12 },
    { id: 'upper',     label: 'One uppercase letter',     test: (p) => /[A-Z]/.test(p) },
    { id: 'lower',     label: 'One lowercase letter',     test: (p) => /[a-z]/.test(p) },
    { id: 'digit',     label: 'One number',               test: (p) => /\d/.test(p) },
    { id: 'symbol',    label: 'One symbol (!@#$…)',       test: (p) => /[^A-Za-z0-9]/.test(p) },
    { id: 'blocklist', label: 'Not a commonly-leaked password',
      test: (p) => p.length > 0 && !BLOCKLIST.has(p.toLowerCase()) },
  ];
  if (email && email.includes('@')) {
    const local = email.split('@')[0].toLowerCase();
    if (local) {
      rules.push({
        id: 'no-email', label: `Doesn't contain your email`,
        test: (p) => p.length > 0 && !p.toLowerCase().includes(local),
      });
    }
  }
  if (name && name.trim()) {
    const tokens = name.trim().split(/\s+/).filter((t) => t.length >= 4).map((t) => t.toLowerCase());
    if (tokens.length) {
      rules.push({
        id: 'no-name', label: `Doesn't contain your name`,
        test: (p) => p.length > 0 && !tokens.some((t) => p.toLowerCase().includes(t)),
      });
    }
  }
  return rules;
}

/* Strength scoring 0-4. Bonus for extra length so a 20-char passphrase
   without symbols still scores Excellent. */
function scoreStrength(password, rules) {
  if (!password) return { score: 0, label: 'Empty', color: '#4B5563' };
  const passing = rules.filter((r) => r.test(password)).length;
  let bonus = 0;
  if (password.length >= 16) bonus += 1;
  if (password.length >= 20) bonus += 1;
  const raw = passing + bonus;
  if (raw <= 2) return { score: 1, label: 'Weak',      color: '#EF4444' };
  if (raw <= 4) return { score: 2, label: 'Fair',      color: '#F59E0B' };
  if (raw <= 6) return { score: 3, label: 'Strong',    color: '#22C55E' };
  return           { score: 4, label: 'Excellent', color: '#AE52EA' };
}

/* Single rule row — animates as its `ok` flag flips. */
function RuleRow({ ok, label, dim, variant, testId }) {
  const dimColor = variant === 'light' ? 'rgba(15,23,42,0.35)' : 'rgba(255,255,255,0.35)';
  const idleColor = variant === 'light' ? 'rgba(51,65,85,0.85)' : 'rgba(255,255,255,0.65)';
  const okColor = variant === 'light' ? '#059669' : '#86EFAC';
  const idleBorder = variant === 'light' ? 'rgba(148,163,184,0.55)' : 'rgba(255,255,255,0.25)';
  return (
    <div
      data-testid={testId}
      className="flex items-center gap-2 text-[11.5px] leading-snug transition-all duration-300 normal-case tracking-normal"
      style={{ color: dim ? dimColor : ok ? okColor : idleColor }}
    >
      <span
        className="inline-flex items-center justify-center h-4 w-4 rounded-full border transition-all duration-300 flex-shrink-0"
        style={{
          borderColor: dim ? (variant === 'light' ? 'rgba(148,163,184,0.35)' : 'rgba(255,255,255,0.15)')
                            : ok ? '#22C55E' : idleBorder,
          background: ok ? 'rgba(34,197,94,0.15)' : 'transparent',
          transform: ok ? 'scale(1.05)' : 'scale(1)',
          boxShadow: ok ? '0 0 12px -2px rgba(34,197,94,0.5)' : 'none',
        }}
      >
        {ok ? (
          <Check size={10} strokeWidth={3} color="#22C55E" />
        ) : (
          <X size={9} strokeWidth={2.5} color={dim ? 'rgba(148,163,184,0.4)' : idleBorder} />
        )}
      </span>
      <span>{label}</span>
    </div>
  );
}

export default function PasswordCreator({
  value,
  onValueChange,
  confirmValue,
  onConfirmChange,
  onValidChange,
  email,
  name,
  variant = 'dark',
  idPrefix = 'pwd',
  autoFocus = false,
  passwordLabel = 'New password',
  confirmLabel = 'Confirm password',
  placeholder = '12+ chars · mix upper/lower/digit/symbol',
  disabled = false,
}) {
  const [show, setShow] = useState(false);
  const isDark = variant !== 'light';

  const rules = useMemo(() => buildRules({ email, name }), [email, name]);
  const ruleResults = useMemo(
    () => rules.map((r) => ({ ...r, ok: r.test(value || '') })),
    [rules, value],
  );
  const allPass = ruleResults.every((r) => r.ok);
  const strength = useMemo(() => scoreStrength(value || '', rules), [value, rules]);
  const matches = (confirmValue || '').length > 0 && value === confirmValue;
  const valid = allPass && matches;

  useEffect(() => {
    if (typeof onValidChange === 'function') onValidChange(valid);
  }, [valid, onValidChange]);

  /* Colour palette per variant. Kept inline (rather than className soup)
     so the component works even in pages that don't extend our Tailwind
     theme (e.g. dropped into an embedded flow). */
  const c = isDark
    ? {
        inputBg: 'rgba(0,0,0,0.4)',
        inputText: '#ffffff',
        inputPlaceholder: 'rgba(255,255,255,0.30)',
        borderIdle: 'rgba(255,255,255,0.15)',
        toggleColor: 'rgba(255,255,255,0.40)',
        toggleHover: '#ffffff',
        labelColor: 'rgba(255,255,255,0.45)',
        rulesBg: 'rgba(255,255,255,0.02)',
        rulesBorder: 'rgba(255,255,255,0.08)',
        segIdle: 'rgba(255,255,255,0.08)',
      }
    : {
        inputBg: '#ffffff',
        inputText: '#0F172A',
        inputPlaceholder: 'rgba(100,116,139,0.60)',
        borderIdle: '#CBD5E1',
        toggleColor: 'rgba(100,116,139,0.85)',
        toggleHover: '#0F172A',
        labelColor: 'rgba(100,116,139,0.90)',
        rulesBg: 'rgba(241,245,249,0.60)',
        rulesBorder: 'rgba(203,213,225,0.80)',
        segIdle: 'rgba(148,163,184,0.25)',
      };

  const inputBorderColor = allPass
    ? '#22C55E'
    : (value || '').length > 0
      ? strength.color
      : c.borderIdle;

  const inputShadow = allPass
    ? '0 0 24px -4px rgba(34,197,94,0.45), 0 0 0 3px rgba(34,197,94,0.10) inset'
    : (value || '').length > 0
      ? `0 0 18px -6px ${strength.color}44`
      : 'none';

  const confirmBorderColor = (confirmValue || '').length === 0
    ? c.borderIdle
    : matches ? '#22C55E' : '#F59E0B';

  const confirmShadow = matches
    ? '0 0 18px -6px rgba(34,197,94,0.45)'
    : (confirmValue || '').length > 0
      ? '0 0 18px -6px rgba(245,158,11,0.35)'
      : 'none';

  return (
    <div className="space-y-4" data-testid={`${idPrefix}-creator`}>
      {/* Password input */}
      <label
        className="block text-[10px] uppercase tracking-[0.22em]"
        style={{ color: c.labelColor }}
      >
        {passwordLabel}
        <div className="relative mt-1.5">
          <input
            type={show ? 'text' : 'password'}
            required
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            disabled={disabled}
            autoFocus={autoFocus}
            data-testid={`${idPrefix}-input`}
            autoComplete="new-password"
            placeholder={placeholder}
            className="w-full rounded-md px-3 py-2.5 pr-10 text-sm focus:outline-none normal-case tracking-normal transition-all duration-300"
            style={{
              backgroundColor: c.inputBg,
              color: c.inputText,
              border: `1px solid ${inputBorderColor}`,
              boxShadow: inputShadow,
            }}
          />
          <style>{`
            [data-testid="${idPrefix}-input"]::placeholder { color: ${c.inputPlaceholder}; }
            [data-testid="${idPrefix}-confirm-input"]::placeholder { color: ${c.inputPlaceholder}; }
          `}</style>
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            aria-label={show ? 'Hide password' : 'Show password'}
            data-testid={`${idPrefix}-toggle`}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 transition-colors"
            style={{ color: c.toggleColor }}
            onMouseEnter={(e) => { e.currentTarget.style.color = c.toggleHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = c.toggleColor; }}
            tabIndex={-1}
          >
            {show ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      </label>

      {/* Strength meter */}
      <div data-testid={`${idPrefix}-strength`} className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: c.labelColor }}>
            Strength
          </span>
          <span
            className="text-[10px] uppercase tracking-[0.22em] font-bold transition-colors duration-300"
            style={{ color: strength.color }}
            data-testid={`${idPrefix}-strength-label`}
          >
            {strength.label}
          </span>
        </div>
        <div className="flex gap-1">
          {[1, 2, 3, 4].map((seg) => (
            <div
              key={seg}
              className="flex-1 h-1.5 rounded-full transition-all duration-500 ease-out"
              style={{
                background: strength.score >= seg ? strength.color : c.segIdle,
                boxShadow: strength.score >= seg ? `0 0 10px -2px ${strength.color}88` : 'none',
              }}
            />
          ))}
        </div>
      </div>

      {/* Rule checklist */}
      <div
        data-testid={`${idPrefix}-rules`}
        className="rounded-md p-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 transition-all duration-500 border"
        style={{
          backgroundColor: c.rulesBg,
          borderColor: c.rulesBorder,
          opacity: (value || '').length === 0 ? 0.8 : 1,
        }}
      >
        {ruleResults.map((r) => (
          <RuleRow
            key={r.id}
            ok={r.ok}
            label={r.label}
            dim={(value || '').length === 0}
            variant={variant}
            testId={`${idPrefix}-rule-${r.id}`}
          />
        ))}
      </div>

      {/* Confirm input */}
      <label className="block text-[10px] uppercase tracking-[0.22em]" style={{ color: c.labelColor }}>
        {confirmLabel}
        <div className="relative mt-1.5">
          <input
            type={show ? 'text' : 'password'}
            required
            value={confirmValue}
            onChange={(e) => onConfirmChange(e.target.value)}
            disabled={disabled}
            data-testid={`${idPrefix}-confirm-input`}
            autoComplete="new-password"
            placeholder="Type it again"
            className="w-full rounded-md px-3 py-2.5 text-sm focus:outline-none normal-case tracking-normal transition-all duration-300"
            style={{
              backgroundColor: c.inputBg,
              color: c.inputText,
              border: `1px solid ${confirmBorderColor}`,
              boxShadow: confirmShadow,
            }}
          />
        </div>
        {(confirmValue || '').length > 0 && (
          <span
            data-testid={`${idPrefix}-match`}
            className="mt-1.5 flex items-center gap-1.5 text-[11px] normal-case tracking-normal transition-colors duration-300"
            style={{ color: matches ? (isDark ? '#86EFAC' : '#059669') : '#B45309' }}
          >
            {matches ? <Check size={11} strokeWidth={3} /> : <X size={11} strokeWidth={2.5} />}
            {matches ? 'Passwords match' : 'Passwords are different'}
          </span>
        )}
      </label>
    </div>
  );
}
